import { useCallback, useState } from 'react'
import { Overlay } from './Overlay'
import { tryKey, type UnlockResult } from './unlock'
import { FULL_APP_PATH } from '../../config'

interface UnlockDialogProps {
  readonly onClose: () => void
  /** Injected in tests; the browser's fetch otherwise. */
  readonly fetcher?: typeof fetch
  /** Injected in tests; a real navigation otherwise. */
  readonly navigate?: (path: string) => void
}

const MESSAGES: Record<Exclude<UnlockResult, 'ok'>, string> = {
  rejected: 'Dieser Key passt nicht.',
  unreachable: 'Der Server war nicht erreichbar. Nochmal versuchen?',
}

/**
 * The key prompt.
 *
 * Checks before it navigates: a wrong key would otherwise land the user on a
 * bare 403 page with no way back and no idea what went wrong.
 */
export function UnlockDialog({ onClose, fetcher, navigate }: UnlockDialogProps): React.JSX.Element {
  const [key, setKey] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isChecking, setChecking] = useState(false)

  const submit = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault()
      setChecking(true)
      setError(null)

      void tryKey(key, fetcher).then((result) => {
        setChecking(false)
        if (result === 'ok') {
          if (navigate) navigate(FULL_APP_PATH)
          else window.location.assign(FULL_APP_PATH)
          return
        }
        setError(MESSAGES[result])
      })
    },
    [fetcher, key, navigate],
  )

  return (
    <Overlay title="Vollversion" onClose={onClose}>
      <p className="overlay__text">
        Mit einem Zugangsschlüssel öffnet sich multitimbral mit allen neun Stimmungen, dem Kammerton
        von 428 bis 454&nbsp;Hz, den Notationen, der Transposition und den gezupften Referenztönen.
      </p>

      <form className="overlay__form" onSubmit={submit}>
        <label className="controls__field">
          <span>Zugangsschlüssel</span>
          <input
            type="password"
            value={key}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label="Zugangsschlüssel"
            aria-invalid={error !== null}
            onChange={(event) => {
              setKey(event.target.value)
              setError(null)
            }}
          />
        </label>

        <button type="submit" className="button" disabled={isChecking || key.trim() === ''}>
          {isChecking ? 'Prüfe …' : 'Freischalten'}
        </button>
      </form>

      {error !== null && (
        <p className="overlay__error" role="alert">
          {error}
        </p>
      )}

      <p className="overlay__note">
        Noch keinen Schlüssel? Über <strong>Spenden</strong> im selben Menü gibt es einen.
      </p>
    </Overlay>
  )
}
